import React, { Fragment } from 'react'
import { Box, TextField, Typography, Chip, useTheme, useMediaQuery } from '@mui/material'
import { CollapsibleInputCard } from '../../../components/_WMS/CollapsibleInputCard'
import { CameraScanButton } from '../../../components/_WMS/CameraScan'
import { useScanKeyboard } from '../../../hooks/useScanKeyboard'
import { LoadingButton } from '../../../components/_EOS/Button/LoadingButton/LoadingButton'
import { PositionAttributeAlert } from '../../../components/UI/PositionAttributeAlert/PositionAttributeAlert'

interface DestinyPositionSectionProps {
  destinyInputRef: React.RefObject<HTMLInputElement>
  localDestinyPosition: string
  setLocalDestinyPosition: (value: string) => void
  destinyValidated: boolean
  destinyValidationFailed: boolean
  isValidating: boolean
  isAutoAssignedPosition: boolean
  autoAssignmentFailed: boolean
  error?: string
  handleValidateDestiny: (scannedValue?: string) => void
  handleEditDestiny: () => void
  sharedState: {
    posicionDestino: string
    isDamaged: boolean
  }
}

export const DestinyPositionSection: React.FC<DestinyPositionSectionProps> = ({
  destinyInputRef,
  localDestinyPosition,
  setLocalDestinyPosition,
  destinyValidated,
  destinyValidationFailed,
  isValidating,
  isAutoAssignedPosition,
  autoAssignmentFailed,
  error,
  handleValidateDestiny,
  handleEditDestiny,
  sharedState
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  // En modo camara el teclado virtual no se levanta salvo que toquen el input.
  const { autoFocus, isCameraCapture } = useScanKeyboard()

  return (
    <Box mb={3}>
      <CollapsibleInputCard
        title="Posición de Destino"
        isValidated={destinyValidated}
        validationFailed={destinyValidationFailed}
        onEdit={handleEditDestiny}
        isMobile={isMobile}
        expandedContent={
          <Fragment>
            {sharedState.isDamaged && isAutoAssignedPosition && (
              <Typography variant="body2" style={{ color: theme.palette.success.main, marginBottom: 16 }}>
                Esta posición fue asignada automáticamente para el producto que requiere ir a calidad. Puede editarla si es necesario.
              </Typography>
            )}
            {sharedState.isDamaged && !isAutoAssignedPosition && !autoAssignmentFailed && (
              <Typography variant="body2" style={{ color: theme.palette.primary.main, marginBottom: 16 }}>
                Se ha pre-llenado con CALIDAD-TRANSFERENCIA para producto que requiere ir a calidad. Puede editar si es necesario.
              </Typography>
            )}
            {sharedState.isDamaged && autoAssignmentFailed && (
              <Typography variant="body2" style={{ color: theme.palette.warning.main, marginBottom: 16 }}>
                No se encontró una posición CALIDAD-TRANSFERENCIA disponible. Ingrese la posición de destino manualmente.
              </Typography>
            )}
            {/* En modo camara siempre columna: el boton de camara va entre el
                input y "Validar", y en fila los tres no entrarian. */}
            <Box display="flex" flexDirection={isMobile || isCameraCapture ? 'column' : 'row'}>
              <TextField
                inputRef={destinyInputRef}
                label="Código de Posición"
                variant="outlined"
                fullWidth
                value={localDestinyPosition}
                onChange={(e) => setLocalDestinyPosition(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleValidateDestiny()
                  }
                }}
                error={destinyValidationFailed}
                style={{
                  marginRight: isMobile || isCameraCapture ? 0 : theme.spacing(1),
                  marginBottom: isMobile || isCameraCapture ? theme.spacing(2) : 0
                }}
              />

              {/* Entre el input y "Validar": es una alternativa para llenar el
                  campo, no una accion sobre lo ya ingresado. */}
              <Box mb={1.5} width="100%">
                <CameraScanButton
                  inputRef={destinyInputRef}
                  onScan={(value) => {
                    setLocalDestinyPosition(value)
                    handleValidateDestiny(value)
                  }}
                  onCancel={() => autoFocus && setTimeout(() => destinyInputRef.current?.focus(), 0)}
                  disabled={isValidating}
                  dialogTitle='Escanear posición de destino'
                  helperText='Centra el código de la posición dentro del recuadro'
                />
              </Box>

              <LoadingButton
                loading={isValidating}
                variant="contained"
                color="primary"
                // Envuelto a proposito: pasarlo directo mandaria el MouseEvent
                // como scannedValue.
                onClick={() => handleValidateDestiny()}
                disabled={!localDestinyPosition.trim()}
                fullWidth={isMobile || isCameraCapture}
                style={{ minWidth: isMobile ? 'auto' : '100px' }}
              >
                Validar
              </LoadingButton>
            </Box>
            {error && <PositionAttributeAlert message={error} variant='body2' />}
          </Fragment>
        }
        collapsedSummary={`Posición Destino: ${sharedState.posicionDestino}`}
        collapsedChips={
          sharedState.isDamaged && (
            <Chip
              label="Requiere Calidad"
              color="secondary"
              size="small"
            />
          )
        }
      />
    </Box>
  )
}